import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import createLazyLoadSlice from './createLazyLoadSlice';
import API_ENDPOINTS from '../../api/apiConfig';
import { getTestListData } from '@/core/utils/getTestListData';
import { NormalizedTestItem, TestItem } from '@/store/types/testItemTypes';
import { testFavoriteSet } from './myTestsSlice';

// Слайс хранит UI-ready форму (NormalizedTestItem) — компоненты получают
// готовые camelCase-поля через useLazyLoad / селекторы, без маппинга на месте.
const resultRecommendationsSlice = createLazyLoadSlice<NormalizedTestItem>('resultRecommendations', {});

// ──────────────────────────────────────────────────────────────────────────
// Нормализация wire-shape → UI-shape (snake → camel, enum status).
// Единственное место, где snake-поля превращаются в camelCase.
// ──────────────────────────────────────────────────────────────────────────
const mapStatus = (s: TestItem['status']): NormalizedTestItem['status'] => {
	if (s === 'passed') return 'passed';
	if (s === 'in_progress') return 'inProgress';
	return 'none';
};

const normalizeTestItem = (item: TestItem): NormalizedTestItem => ({
	id: item.id,
	url: item.url,
	name: item.name,
	previewPicture: item.preview_picture,
	isPro: item.is_pro ?? false,
	isFavorite: item.is_favorite ?? false,
	status: mapStatus(item.status ?? null),
});

const combinedSlice = createSlice({
	name: 'resultRecommendations',
	initialState: resultRecommendationsSlice.getInitialState(),
	reducers: {
		...resultRecommendationsSlice.caseReducers,

		// Перекрываем базовый setDataLoaded: принимаем wire-shape (TestItem[]),
		// кладём в стор UI-shape (NormalizedTestItem[]).
		setDataLoaded(state, action: PayloadAction<TestItem[]>) {
			const normalized = action.payload.map(normalizeTestItem);
			state.data = Array.from(new Set([...state.data, ...normalized])) as [];
			state.isFetching = false;
		},
	},
	// Слушаем cross-slice action (см. `myTestsSlice.ts` → `testFavoriteSet`).
	// Сердечко на карточке `TestListItem` дергают в любом списке — флаг
	// `isFavorite` обновляется сразу в обоих хранилищах, чтобы списки
	// «Мои тесты» и «Рекомендации» оставались в синхроне.
	extraReducers: (builder) => {
		builder.addCase(testFavoriteSet, (state, action) => {
			const { id, isFavorite } = action.payload;
			const item = state.data.find((t) => t.id === id);
			if (item) {
				item.isFavorite = isFavorite;
			}
		});
	},
});

export const {
	incrementPage,
	fetchDataStart,
	setDataLoaded,
	setLastPage,
	fetchDataError
} = combinedSlice.actions;

export const getRecommendations = (testId: number) =>
	getTestListData(combinedSlice, `${API_ENDPOINTS.recommendations}${testId}?page=`);

export default combinedSlice.reducer;
export { combinedSlice as resultRecommendationsSlice };
